/**
 * Run-level exports. Section 12.4, shared component.
 *
 * These are plain links, not fetch-then-blob: the browser's own download
 * handling gets the filename from Content-Disposition and streams a 40 MB
 * shapefile without holding it in memory. Section 13 is graded - the shapefile
 * must open in QGIS and the KML in Google Earth - so nothing here transforms
 * what the backend produced.
 *
 * The settlement CSV is added in Phase 5, when settlement impacts exist.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Download } from 'lucide-react'

import { API_BASE, api, exportUrl } from '../api/client'

const EXPORTS = [
  { kind: 'shapefile', label: 'Shapefile', hint: 'zipped, in {crs}, for QGIS and ArcGIS' },
  { kind: 'kml', label: 'KML', hint: 'Google Earth, with a time slider of its own' },
  { kind: 'kmz', label: 'KMZ', hint: 'the same KML, zipped' },
  { kind: 'csv', label: 'CSV', hint: 'run-level summary' },
  { kind: 'report', label: 'PDF report', hint: 'scenario, headline results, hydrograph' },
] as const

const GEOTIFF_LAYERS = [
  { value: 'max_depth', label: 'Max depth' },
  { value: 'max_velocity', label: 'Max velocity' },
  { value: 'max_dv', label: 'Depth-velocity' },
  { value: 'arrival', label: 'Arrival time' },
  { value: 'duration', label: 'Duration' },
  { value: 'hazard', label: 'Hazard' },
]

export function ExportBar({ runId }: { runId: string }) {
  const [layer, setLayer] = useState('max_depth')
  const [copied, setCopied] = useState(false)
  // The export CRS is the site's model CRS, served by /study-area - not a literal.
  const study = useQuery({ queryKey: ['study-area'], queryFn: api.studyArea })
  const crs = study.data?.crs_projected ?? 'the model CRS'

  const copyApiUrl = async () => {
    const url = new URL(`${API_BASE}/runs/${runId}/summary`, window.location.origin)
    await navigator.clipboard.writeText(url.toString())
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {EXPORTS.map((item) => (
        <a
          key={item.kind}
          href={exportUrl(runId, item.kind)}
          title={item.hint.replace('{crs}', crs)}
          className="inline-flex items-center gap-1.5 rounded border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-xs text-slate-200 hover:border-slate-600 hover:bg-slate-700"
        >
          <Download className="h-3 w-3" />
          {item.label}
        </a>
      ))}

      <span className="inline-flex items-center overflow-hidden rounded border border-slate-700 bg-slate-800">
        <select
          value={layer}
          onChange={(event) => setLayer(event.target.value)}
          className="border-0 bg-slate-800 py-1.5 pl-2 pr-1 text-xs text-slate-200 focus:outline-none"
          aria-label="GeoTIFF layer"
        >
          {GEOTIFF_LAYERS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <a
          href={exportUrl(runId, `geotiff/${layer}`)}
          title={`Cloud-Optimised GeoTIFF, ${crs}`}
          className="inline-flex items-center gap-1.5 border-l border-slate-700 px-2.5 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
        >
          <Download className="h-3 w-3" />
          GeoTIFF
        </a>
      </span>

      <button
        type="button"
        onClick={copyApiUrl}
        className="inline-flex items-center gap-1.5 rounded border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-700"
      >
        {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copied' : 'Copy API URL'}
      </button>
    </div>
  )
}
